import {
  Box, Checkbox, Chip, FormControl, FormControlLabel, FormGroup, FormHelperText, InputLabel,
  MenuItem, Radio, RadioGroup, Select, Stack, TextField, Typography,
} from '@mui/material';
import { ISurveyQuestion, ISurveyQuestionGroupItem, ISurveyResponseItem } from '../../../../shared/services/survey/survey.model';
import { isItemRequired } from './respond-utils';

/** Bounds feedback for numeric answers, shared by every numeric-capable input. */
function numericBounds(q: ISurveyQuestion, value: number | null | undefined) {
  const num = value != null ? Number(value) : null;
  const belowMin = num != null && q.minValue != null && num < q.minValue;
  const aboveMax = num != null && q.maxValue != null && num > q.maxValue;
  return {
    error: belowMin || aboveMax,
    helperText: belowMin ? `Must be at least ${q.minValue}` : aboveMax ? `Must be at most ${q.maxValue}` : undefined,
  };
}

function QuestionPrompt({ headingId, prompt, isRequired, helpText }: {
  headingId: string;
  prompt: string;
  isRequired: boolean;
  helpText?: string | null;
}) {
  return (
    <Box>
      <Typography id={headingId} variant='subtitle1' sx={{ fontWeight: 600, lineHeight: 1.35 }}>
        {prompt}
        {isRequired && (
          <Typography component='span' sx={{ color: 'error.main', fontWeight: 600 }}>
            {' *'}
          </Typography>
        )}
      </Typography>
      {helpText ? (
        <Typography variant='body2' color='text.secondary' sx={{ mt: 0.75 }}>
          {helpText}
        </Typography>
      ) : null}
    </Box>
  );
}

interface AnswerControlProps {
  item: ISurveyQuestionGroupItem;
  answer: ISurveyResponseItem | undefined;
  onChange: (answer: ISurveyResponseItem) => void;
  headingId: string;
  isRequired: boolean;
  /** Set when the respondent tried to submit: required-but-empty answers show their error. */
  showValidation: boolean;
}

type Base = Pick<ISurveyResponseItem, 'surveyQuestionId' | 'surveyQuestionGroupItemId'>;

const NumericAnswer = ({ item, answer, onChange, headingId, isRequired, showValidation, base }: AnswerControlProps & { base: Base }) => {
  const q = item.surveyQuestion!;
  const bounds = numericBounds(q, answer?.numericValue);
  const missing = showValidation && isRequired
    && !(answer?.numericValue != null && Number.isFinite(Number(answer.numericValue)));
  return (
    <TextField
      hiddenLabel
      type='number'
      value={answer?.numericValue ?? ''}
      onChange={e => onChange({
        ...base,
        numericValue: e.target.value === '' ? null : Number(e.target.value),
        textValue: null,
      })}
      fullWidth
      required={isRequired}
      error={bounds.error || missing}
      helperText={bounds.helperText ?? (missing ? 'This question needs an answer' : undefined)}
      inputProps={{ step: 'any', min: q.minValue ?? undefined, max: q.maxValue ?? undefined, 'aria-labelledby': headingId }}
    />
  );
};

const TextAnswer = ({ item, answer, onChange, headingId, isRequired, showValidation, base, multiline }: AnswerControlProps & { base: Base; multiline: boolean }) => {
  const missing = showValidation && isRequired && !answer?.textValue?.trim();
  return (
    <TextField
      hiddenLabel
      fullWidth
      required={isRequired}
      multiline={multiline}
      rows={multiline ? 4 : undefined}
      value={answer?.textValue ?? ''}
      onChange={e => onChange({ ...base, textValue: e.target.value })}
      error={missing}
      helperText={missing ? 'This question needs an answer' : undefined}
      inputProps={{ 'aria-labelledby': headingId }}
    />
  );
};

const DateAnswer = ({ answer, onChange, headingId, isRequired, showValidation, base }: AnswerControlProps & { base: Base }) => {
  const missing = showValidation && isRequired && !answer?.textValue?.trim();
  return (
    <TextField
      hiddenLabel
      type='date'
      value={answer?.textValue ?? ''}
      onChange={e => onChange({ ...base, textValue: e.target.value })}
      required={isRequired}
      fullWidth
      error={missing}
      helperText={missing ? 'This question needs an answer' : undefined}
      InputLabelProps={{ shrink: true }}
      inputProps={{ 'aria-labelledby': headingId }}
    />
  );
};

const ChoiceSingleAnswer = ({ item, answer, onChange, headingId, isRequired, showValidation, base, asRadio }: AnswerControlProps & { base: Base; asRadio: boolean }) => {
  const q = item.surveyQuestion!;
  const options = q.surveyQuestionOptions ?? [];
  const missing = showValidation && isRequired && (answer?.optionIds ?? []).length === 0;
  if (asRadio) {
    return (
      <FormControl required={isRequired} component='fieldset' variant='standard' sx={{ m: 0 }} error={missing}>
        <RadioGroup
          aria-labelledby={headingId}
          value={answer?.optionIds?.[0] ?? ''}
          onChange={e => onChange({ ...base, optionIds: [e.target.value] })}
        >
          {options.map(opt => (
            <FormControlLabel key={opt.id} value={opt.id} control={<Radio />} label={opt.label} />
          ))}
        </RadioGroup>
        {missing && <FormHelperText>This question needs an answer</FormHelperText>}
      </FormControl>
    );
  }
  const labelId = `${headingId}-select-one`;
  return (
    <FormControl fullWidth required={isRequired} error={missing}>
      <InputLabel id={labelId}>Choose an option</InputLabel>
      <Select
        labelId={labelId}
        label='Choose an option'
        value={answer?.optionIds?.[0] ?? ''}
        onChange={e => onChange({ ...base, optionIds: e.target.value ? [e.target.value as string] : [] })}
        inputProps={{ 'aria-labelledby': `${headingId} ${labelId}` }}
      >
        {options.map(opt => <MenuItem key={opt.id} value={opt.id}>{opt.label}</MenuItem>)}
      </Select>
      {missing && <FormHelperText>This question needs an answer</FormHelperText>}
    </FormControl>
  );
};

const ChoiceMultiAnswer = ({ item, answer, onChange, headingId, isRequired, showValidation, base, asCheckboxes }: AnswerControlProps & { base: Base; asCheckboxes: boolean }) => {
  const q = item.surveyQuestion!;
  const options = q.surveyQuestionOptions ?? [];
  const selectedIds = answer?.optionIds ?? [];
  const missing = showValidation && isRequired && selectedIds.length === 0;
  if (asCheckboxes) {
    return (
      <FormControl required={isRequired} component='fieldset' variant='standard' sx={{ m: 0 }} error={missing}>
        <FormGroup aria-labelledby={headingId}>
          {options.map(opt => (
            <FormControlLabel
              key={opt.id}
              control={(
                <Checkbox
                  checked={selectedIds.includes(opt.id)}
                  onChange={e => {
                    const newIds = e.target.checked
                      ? [...selectedIds, opt.id]
                      : selectedIds.filter(id => id !== opt.id);
                    onChange({ ...base, optionIds: newIds });
                  }}
                />
              )}
              label={opt.label}
            />
          ))}
        </FormGroup>
        {missing && <FormHelperText>This question needs an answer</FormHelperText>}
      </FormControl>
    );
  }
  const labelId = `${headingId}-select-multi`;
  return (
    <FormControl fullWidth required={isRequired} error={missing}>
      <InputLabel id={labelId}>Select all that apply</InputLabel>
      <Select
        labelId={labelId}
        label='Select all that apply'
        multiple
        value={selectedIds}
        onChange={e => {
          const val = e.target.value;
          onChange({ ...base, optionIds: typeof val === 'string' ? [val] : val });
        }}
        renderValue={selected => (
          <Stack direction='row' spacing={0.5} flexWrap='wrap'>
            {(selected as string[]).map(id => {
              const opt = options.find(o => o.id === id);
              return <Chip key={id} label={opt?.label ?? id} size='small' />;
            })}
          </Stack>
        )}
        inputProps={{ 'aria-labelledby': `${headingId} ${labelId}` }}
      >
        {options.map(opt => (
          <MenuItem key={opt.id} value={opt.id}>
            <Checkbox checked={selectedIds.includes(opt.id)} />
            {opt.label}
          </MenuItem>
        ))}
      </Select>
      {missing && <FormHelperText>This question needs an answer</FormHelperText>}
    </FormControl>
  );
};

interface QuestionInputProps {
  item: ISurveyQuestionGroupItem;
  answer: ISurveyResponseItem | undefined;
  onChange: (answer: ISurveyResponseItem) => void;
  /** Set after a submit attempt so required-but-empty answers explain themselves. */
  showValidation?: boolean;
}

/** One respondent-facing question: prompt plus the input for its type. */
export const QuestionInput = ({ item, answer, onChange, showValidation = false }: QuestionInputProps) => {
  const q = item.surveyQuestion;
  if (!q) return null;

  const type = q.surveyQuestionType?.code;
  const isRequired = isItemRequired(item);
  const headingId = `survey-q-${q.id}`;
  const base: Base = {
    surveyQuestionId: q.id,
    surveyQuestionGroupItemId: item.id,
  };
  const controlProps = { item, answer, onChange, headingId, isRequired, showValidation, base };

  let control;
  if (type === 'NUMBER' || ((type === 'TEXT_SINGLE_LINE' || type === 'TEXT_MULTI_LINE') && q.enforceNumericAnswer)) {
    control = <NumericAnswer {...controlProps} />;
  } else if (type === 'TEXT_MULTI_LINE') {
    control = <TextAnswer {...controlProps} multiline />;
  } else if (type === 'DATE') {
    control = <DateAnswer {...controlProps} />;
  } else if (type === 'SELECT_SINGLE' || type === 'RADIO') {
    control = <ChoiceSingleAnswer {...controlProps} asRadio={type === 'RADIO'} />;
  } else if (type === 'SELECT_MULTI' || type === 'CHECKBOX_GROUP') {
    control = <ChoiceMultiAnswer {...controlProps} asCheckboxes={type === 'CHECKBOX_GROUP'} />;
  } else {
    // TEXT_SINGLE_LINE and the no-type fallback both render a single-line text field.
    control = <TextAnswer {...controlProps} multiline={false} />;
  }

  return (
    <Stack spacing={1.25}>
      <QuestionPrompt headingId={headingId} prompt={q.prompt} isRequired={isRequired} helpText={q.helpText} />
      {control}
    </Stack>
  );
};
